import { z } from 'zod';

/** Which WhatsApp gateway delivers the codes.
 *  'greenapi' = Green API cloud; 'evolution' = the self-hosted Evolution API
 *  gateway (instanceId = instance name, apiToken = per-instance token). */
export const OtpProviderSchema = z.enum(['greenapi', 'evolution']);
export type OtpProvider = z.infer<typeof OtpProviderSchema>;

/** Public view of the tenant's OTP (WhatsApp gateway) config — token never returned. */
export interface OtpSettings {
  provider: OtpProvider;
  hasToken: boolean;
  instanceId: string | null;
  apiUrl: string;
  messageTemplate: string;
  codeLength: number;
  ttlSeconds: number;
  isActive: boolean;
  updatedAt: string;
  /**
   * Why the last code failed to leave, or null when the last one went out. The
   * SEND response stays generic on purpose (it must not reveal whether a number
   * is registered) — this is the shop's own diagnostic, and the only thing that
   * would have shown a re-linked WhatsApp had invalidated the saved token.
   */
  lastSendError: string | null;
  lastSendAt: string | null;
}

export const OtpSettingsUpdateSchema = z.object({
  provider: OtpProviderSchema.optional(),
  instanceId: z.string().max(64).nullable().optional(),
  /** Write-only — stored encrypted; omit to keep the existing token. */
  apiToken: z.string().max(500).optional(),
  apiUrl: z.string().url().max(191).optional(),
  messageTemplate: z.string().min(4).max(500).optional(),
  codeLength: z.number().int().min(4).max(8).optional(),
  ttlSeconds: z.number().int().min(60).max(900).optional(),
  isActive: z.boolean().optional(),
});
export type OtpSettingsUpdate = z.infer<typeof OtpSettingsUpdateSchema>;

/** A phone number to send a code to. Kept loose — normalized server-side to digits. */
export const OtpRequestSchema = z.object({
  phone: z.string().min(6).max(32),
});
export type OtpRequest = z.infer<typeof OtpRequestSchema>;

export const OtpVerifySchema = z.object({
  phone: z.string().min(6).max(32),
  code: z.string().min(4).max(8),
});
export type OtpVerify = z.infer<typeof OtpVerifySchema>;

/** Request result. `sent` and `ttlSeconds` are deliberately generic for most
 *  paths. `registered` is the exception, and it is symmetric:
 *    LOGIN    — `false` when the phone maps to no account, so the client stops
 *               before the code screen instead of waiting for a code that never
 *               arrives.
 *    REGISTER — `true` when the phone ALREADY has an account, so the client says
 *               "sign in instead" WITHOUT a code being sent. Sending first and
 *               refusing afterwards spent a real WhatsApp message, and told the
 *               customer to start over only once they had typed the code out.
 *  Both directions disclose the same fact, and the storefront already accepted
 *  that trade for login. Absent on the reset flow, which proceeds regardless. */
export interface OtpRequestResult {
  sent: boolean;
  ttlSeconds: number;
  registered?: boolean;
}
